import {XMLBuilder} from 'fast-xml-parser';

import Item from '~/shared/src/ci/Item';
import {
    getItemTypeXmlJson,
    getPropertySetXmlJson,
    getPropertyTypeXmlJson,
    getReferenceSetXmlJson,
    getReferenceTypeXmlJson,
    getValueSetXmlJson,
    getValueXmlJson
} from '~/shared/src/utilities/xmlMetaModelTools';
import metaModel from '~/shared/src/utilities/metaModel';
import dataUtils from '~/shared/src/utilities/dataUtils';
import {wrapDataXml} from '~/shared/src/utilities/xmlExport';

/**
 * XMLBuilder Class with modified version of the attribute pair builder using single quotes
 */
class YNXmlBuilder extends XMLBuilder {

    /**
     * Modified version of the attribute pair builder using single quotes
     * @param attrName
     * @param val
     */
    buildAttrPairStr( attrName : string, val : string ) : string {
        // @ts-expect-error as private variable options is not declared in .d.ts file
        val = this.options.attributeValueProcessor( attrName, '' + val );
        // @ts-expect-error as private function replaceEntitiesValue is not declared in .d.ts file
        val = this.replaceEntitiesValue( val );
        // @ts-expect-error as private variable options is not declared in .d.ts file
        if ( this.options.suppressBooleanAttributes && val === 'true' ) {
            return ' ' + attrName;
        }
        else {
            return ' ' + attrName + '=\'' + val + '\'';
        }
    }

    buildObjectNode( val : string, key : string, attrStr : string, level : string ) : string {
        // eslint-disable-next-line @typescript-eslint/ban-ts-comment
        // @ts-expect-error
        let retVal : string = super.buildObjectNode( val, key, attrStr, level );
        // eslint-disable-next-line @typescript-eslint/ban-ts-comment
        // @ts-expect-error
        const lines = retVal.split( this.newLine );
        if ( lines.length === 4 && lines[ 1 ].includes( '<![CDATA[' ) ) {
            // eslint-disable-next-line @typescript-eslint/ban-ts-comment
            // @ts-expect-error
            retVal = lines[ 0 ] + lines[ 1 ].trim() + lines[ 2 ].trim() + this.newLine;
        }
        return retVal;
    }
}

/**
 *
 * @param {string} dataXml
 * @param {Schema} schema
 */
export const getXmlForDataXml = ( dataXml : string, schema : Schema | 'data' | 'cfg' ) : string => {
    let data : string = '';

    data += `<metaModel schema='${schema.toLowerCase()}'>\r\n`.indent();
    data += dataXml;
    data += `</metaModel>\r\n`.indent();

    return wrapDataXml( data );
}

/**
 *
 * @param {string} dataContext
 * @param {number[]} [itemIdsToInclude]           If set the itemIds to be included in export
 * @param {number[]} [referenceIdsToInclude]      If set the referenceIds to be included in export
 * @param {boolean} [excludeDeleted]
 */
export const getMetaModelXmlForContext = ( dataContext : string, itemIdsToInclude? : number[], referenceIdsToInclude? : number[], excludeDeleted : boolean = false, ynItemTypeItemId? : number ) : string => {
    let retVal : string = '';

    if ( global.data[ dataContext ] ) {
        const schemaPropertyTypeId = metaModel.CFG.getPropertyType( 'Schema' )?.id ?? 0;
        const schema = Object.values( global.data[ dataContext ].items )[ 0 ]?.properties.get( schemaPropertyTypeId )?.value as 'data' | 'cfg' ?? 'data';
        retVal = getXmlForDataXml( getXmlMetaModelPayloadForContext( dataContext, itemIdsToInclude, referenceIdsToInclude, excludeDeleted, ynItemTypeItemId ), schema );
    }

    return retVal;
}

/**
 *
 * @param {string} dataContext
 * @param {number[]} [itemIdsToInclude]           If set the itemIds to be included in export
 * @param {number[]} [referenceIdsToInclude]      If set the referenceIds to be included in export
 * @param {boolean} [excludeDeleted]
 */
export const getXmlMetaModelPayloadForContext = ( dataContext : string, itemIdsToInclude? : number[], referenceIdsToInclude? : number[], excludeDeleted : boolean = false, ynItemTypeItemId? : number ) : string => {
    let retVal : string = '';

    const builder = new YNXmlBuilder( {
        attributeNamePrefix: '@_',
        ignoreAttributes   : false,
        format             : true,
        cdataPropName      : '#cdata',
        indentBy           : ''.indent()
    } );

    if ( global.data[ dataContext ] ) {
        const items : Item[]                                                              = Object.values( global.data[ dataContext ].items ).filter( item => excludeDeleted ? !item.isDeleted() : true ),
              //references : Reference[] = Object.values( global.data[ dataContext ].references ).filter( reference => excludeDeleted ? !reference.isDeleted() : true ),
              exportedUuids : string[]                                                    = [],
              isPropertySetForRsetId                                                      = metaModel.CFG.getReferenceSetsForItemTypesAndReferenceType( 'YnPropertySet', 'YnItemType', 'isPropertySetFor' )[ 0 ].id,
              isKeyLabelForPropertySetRsetId                                              = metaModel.CFG.getReferenceSetsForItemTypesAndReferenceType( 'Message', 'YnPropertySet', 'isKeyLabelFor' )[ 0 ].id,
              isValueLabelForPropertySetRsetId                                            = metaModel.CFG.getReferenceSetsForItemTypesAndReferenceType( 'Message', 'YnPropertySet', 'isValueLabelFor' )[ 0 ].id,
              isTypeLabelForPropertySetRsetId                                             = metaModel.CFG.getReferenceSetsForItemTypesAndReferenceType( 'Message', 'YnPropertySet', 'isTypeLabelFor' )[ 0 ].id,
              isReferencePropertySetForRsetId                                             = metaModel.CFG.getReferenceSetsForItemTypesAndReferenceType( 'YnReferencePropertySet', 'YnReferenceSet', 'isPropertySetFor' )[ 0 ].id,
              isKeyLabelForReferencePropertySetRsetId                                     = metaModel.CFG.getReferenceSetsForItemTypesAndReferenceType( 'Message', 'YnReferencePropertySet', 'isKeyLabelFor' )[ 0 ].id,
              isValueLabelForReferencePropertySetRsetId                                   = metaModel.CFG.getReferenceSetsForItemTypesAndReferenceType( 'Message', 'YnReferencePropertySet', 'isValueLabelFor' )[ 0 ].id,
              isTypeLabelForReferencePropertySetRsetId                                    = metaModel.CFG.getReferenceSetsForItemTypesAndReferenceType( 'Message', 'YnReferencePropertySet', 'isTypeLabelFor' )[ 0 ].id,
              isKeyLabelForItemTypeRsetId                                                 = metaModel.CFG.getReferenceSetsForItemTypesAndReferenceType( 'Message', 'YnItemType', 'isKeyLabelFor' )[ 0 ].id,
              isNameLabelForItemTypeRsetId                                                = metaModel.CFG.getReferenceSetsForItemTypesAndReferenceType( 'Message', 'YnItemType', 'isNameLabelFor' )[ 0 ].id,
              isTypeLabelForItemTypeRsetId                                                = metaModel.CFG.getReferenceSetsForItemTypesAndReferenceType( 'Message', 'YnItemType', 'isTypeLabelFor' )[ 0 ].id,
              isIconForItemTypeRsetId                                                     = metaModel.CFG.getReferenceSetsForItemTypesAndReferenceType( 'Image', 'YnItemType', 'isIconFor' )[ 0 ].id,
              isValueSetForPropertySetRsetId                                              = metaModel.CFG.getReferenceSetsForItemTypesAndReferenceType( 'YnValueSet', 'YnPropertySet', 'isValueSetFor' )[ 0 ].id,
              isValueSetForReferencePropertySetRsetId                                     = metaModel.CFG.getReferenceSetsForItemTypesAndReferenceType( 'YnValueSet', 'YnReferencePropertySet', 'isValueSetFor' )[ 0 ].id,
              isValueForValueSetRsetId                                                    = metaModel.CFG.getReferenceSetsForItemTypesAndReferenceType( 'YnValue', 'YnValueSet', 'isValueFor' )[ 0 ].id,
              isLabelForValueRsetId                                                       = metaModel.CFG.getReferenceSetsForItemTypesAndReferenceType( 'Message', 'YnValue', 'isLabelFor' )[ 0 ].id,
              propertySetToValueSetMap : { [ propertySetId : number ] : string }          = {},
              referencePropertySetToValueSetMap : { [ propertySetId : number ] : string } = {},
              propertySets : { [ ynItemTypeId : number ] : any[] }                        = {},
              referencePropertySets : { [ ynReferenceSetId : number ] : any[] }           = {},
              referenceTypesSort : { sortLabel : string; xml : string; }[]                = [],
              referenceSetsSort : { sortLabel : string; xml : string; }[]                 = [];

        let itemTypesXml : string     = '',
            propertyTypesXml : string = '',
            valueSetsXml : string     = '';

        items.sort( ( item1, item2 ) => {
            const sortKey1 = item1.key.toLowerCase(), //`${item1.itemType.name}::${item1.key}`.toLowerCase(),
                  sortKey2 = item2.key.toLowerCase(); //`${item2.itemType.name}::${item2.key}`.toLowerCase();

            return sortKey1 > sortKey2 ? 1 : sortKey1 < sortKey2 ? -1 : 0;
        } );

        // export valueSets and values
        for ( const item of items.filter( i => i.itemType.name === 'YnValueSet' ) ) {
            const values = dataUtils.findReferences( dataContext, isValueForValueSetRsetId, undefined, item.id )
                ?.map( ref => {
                    const valueLabelItem = dataUtils.findReferences( dataContext, isLabelForValueRsetId, undefined, ref.srcItem!.id )
                        ?.[ 0 ]
                        ?.srcItem;

                    return getValueXmlJson( ref.destItem!, ref.srcItem!, valueLabelItem );
                } );

            valueSetsXml += builder.build( { valueSet: { ...getValueSetXmlJson( item ), value: values } } ).split( '\n' ).slice( 0, -1 ).map( ( line : string ) => line.indent( 2 ) ).concat( '' ).join( '\r\n' );

            dataUtils.findReferences( dataContext, isValueSetForPropertySetRsetId, item.id )
                ?.reduce(
                    ( result, ref ) => {
                        result[ ref.destItemId ] = item.name;
                        return result;
                    },
                    propertySetToValueSetMap
                );
            dataUtils.findReferences( dataContext, isValueSetForReferencePropertySetRsetId, item.id )
                ?.reduce(
                    ( result, ref ) => {
                        result[ ref.destItemId ] = item.name;
                        return result;
                    },
                    referencePropertySetToValueSetMap
                );

        }

        // export propertyTypes and collect propertySets for each item type
        for ( const item of items.filter( i => i.itemType.name === 'YnPropertySet' || i.itemType.name === 'YnReferencePropertySet' ) ) {
            if ( !itemIdsToInclude || itemIdsToInclude.includes( item.id ) ) {
                const propertyType = getPropertyTypeXmlJson( item );

                if ( !exportedUuids.includes( propertyType[ '@_uuid' ] ) ) {
                    propertyTypesXml += builder.build( { propertyType } )
                        .split( '\n' )
                        .slice( 0, -1 )
                        .map( ( line : string ) => line.indent( 2 ) )
                        .concat( '' )
                        .join( '\r\n' );
                    exportedUuids.push( propertyType[ '@_uuid' ] );
                }

                if ( item.itemType.name === 'YnPropertySet' ) {
                    // get related YnItemType item
                    const ynItemType = dataUtils.findReferences( dataContext, isPropertySetForRsetId, item.id )?.[ 0 ].destItem;

                    if ( ynItemType ) {
                        const typeLabelItem  = dataUtils.findReferences( dataContext, isTypeLabelForPropertySetRsetId, undefined, item.id )?.[ 0 ]?.srcItem,
                              valueLabelItem = dataUtils.findReferences( dataContext, isValueLabelForPropertySetRsetId, undefined, item.id )?.[ 0 ]?.srcItem,
                              keyLabelItem   = dataUtils.findReferences( dataContext, isKeyLabelForPropertySetRsetId, undefined, item.id )?.[ 0 ]?.srcItem;

                        propertySets[ ynItemType.id ] = propertySets[ ynItemType.id ] ?? [];
                        propertySets[ ynItemType.id ].push( getPropertySetXmlJson( item, typeLabelItem, valueLabelItem, keyLabelItem, propertySetToValueSetMap[ item.id ], ynItemType ) );
                    }
                }
                else {
                    // get related YnReferenceSet item
                    const ynReferenceSet = dataUtils.findReferences( dataContext, isReferencePropertySetForRsetId, item.id )?.[ 0 ].destItem;

                    if ( ynReferenceSet ) {
                        const typeLabelItem  = dataUtils.findReferences( dataContext, isTypeLabelForReferencePropertySetRsetId, undefined, item.id )?.[ 0 ]?.srcItem,
                              valueLabelItem = dataUtils.findReferences( dataContext, isValueLabelForReferencePropertySetRsetId, undefined, item.id )?.[ 0 ]?.srcItem,
                              keyLabelItem   = dataUtils.findReferences( dataContext, isKeyLabelForReferencePropertySetRsetId, undefined, item.id )?.[ 0 ]?.srcItem;

                        referencePropertySets[ ynReferenceSet.id ] = referencePropertySets[ ynReferenceSet.id ] ?? [];
                        referencePropertySets[ ynReferenceSet.id ].push( getPropertySetXmlJson( item, typeLabelItem, valueLabelItem, keyLabelItem, referencePropertySetToValueSetMap[ item.id ], ynReferenceSet ) );
                    }
                }

            }
        }

        // export itemTypes with propertySets
        for ( const item of items.filter( i => i.itemType.name === 'YnItemType' && ( !ynItemTypeItemId || ynItemTypeItemId === i.id ) ) ) {
            if ( !itemIdsToInclude || itemIdsToInclude.includes( item.id ) ) {
                const typeLabelItem = dataUtils.findReferences( dataContext, isTypeLabelForItemTypeRsetId, undefined, item.id )?.[ 0 ]?.srcItem,
                      nameLabelItem = dataUtils.findReferences( dataContext, isNameLabelForItemTypeRsetId, undefined, item.id )?.[ 0 ]?.srcItem,
                      keyLabelItem  = dataUtils.findReferences( dataContext, isKeyLabelForItemTypeRsetId, undefined, item.id )?.[ 0 ]?.srcItem,
                      iconItem      = dataUtils.findReferences( dataContext, isIconForItemTypeRsetId, undefined, item.id )?.[ 0 ]?.srcItem;

                itemTypesXml += builder.build( {
                        itemType: {
                            ...getItemTypeXmlJson( item, typeLabelItem, keyLabelItem, nameLabelItem, iconItem ),
                            propertySet: propertySets[ item.id ]
                        }
                    } )
                    .split( '\n' )
                    .slice( 0, -1 )
                    .map( ( line : string ) => line.indent( 2 ) )
                    .concat( '' )
                    .join( '\r\n' );
            }
        }

        // export referenceSets with referencePropertySets
        for ( const item of items.filter( i => i.itemType.name === 'YnReferenceSet' ) ) {
            if ( !itemIdsToInclude || itemIdsToInclude.includes( item.id ) ) {
                const referenceType = getReferenceTypeXmlJson( item ),
                      referenceSet  = getReferenceSetXmlJson( item, dataContext );

                if ( !exportedUuids.includes( referenceType[ '@_uuid' ] ) ) {
                    referenceTypesSort.push( {
                        sortLabel: referenceType[ '@_name' ],
                        xml      : builder.build( { referenceType } )
                            .split( '\n' )
                            .slice( 0, -1 )
                            .map( ( line : string ) => line.indent( 2 ) )
                            .concat( '' )
                            .join( '\r\n' )
                    } );
                    exportedUuids.push( referenceType[ '@_uuid' ] )
                }

                referenceSetsSort.push( {
                    sortLabel: `${referenceSet.sourceItemType}${referenceSet.referenceType}${referenceSet.destinationItemType}`,
                    xml      : builder.build( {
                            referenceSet: {
                                ...referenceSet,
                                propertySet: referencePropertySets[ item.id ]
                            }
                        } )
                        .split( '\n' )
                        .slice( 0, -1 )
                        .map( ( line : string ) => line.indent( 2 ) )
                        .concat( '' )
                        .join( '\r\n' )
                } );
            }
        }

        const referenceTypesXml = referenceTypesSort.sort( ( v1, v2 ) => {
                      if ( v1.sortLabel > v2.sortLabel ) {
                          return 1;
                      }
                      else if ( v1.sortLabel < v2.sortLabel ) {
                          return -1;
                      }
                      else {
                          return 0;
                      }
                  } )
                  .map( v => v.xml )
                  .join( '' ),
              referenceSetsXml  = referenceSetsSort.sort( ( v1, v2 ) => {
                      if ( v1.sortLabel > v2.sortLabel ) {
                          return 1;
                      }
                      else if ( v1.sortLabel < v2.sortLabel ) {
                          return -1;
                      }
                      else {
                          return 0;
                      }
                  } )
                  .map( v => v.xml )
                  .join( '' );

        // put all in the right order
        retVal = propertyTypesXml + valueSetsXml + referenceTypesXml + itemTypesXml + referenceSetsXml;
    }

    return retVal;
}
